import { ChangeDetectionStrategy, Component, computed, input, output, ViewEncapsulation } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Card } from '../card/card';
import { ZagIcon } from '../icon/icon';
import { ZagButton } from '../button/button';

/**
 * Data structure for a swap/takeover request card.
 * Mirrors SwapRequest but avoids cross-library dependency.
 */
export interface SwapRequestCardData {
  /** Unique request ID */
  id: string;
  /** Whether this is a swap or takeover (matches ShiftApplicationType enum values) */
  type: 'swap' | 'takeover';
  /** Current status of the request (matches ShiftApplicationStatus enum values) */
  status: 'pending' | 'pending_planner_approval' | 'accepted' | 'declined' | 'cancelled' | 'expired' | 'superseded';
  /** When the request was created (ISO 8601) */
  createdAt: string;
  /** Formatted creation date for display */
  createdAtLabel: string;

  /** Whether the current user is the requester (outgoing) or the target (incoming) */
  direction: 'incoming' | 'outgoing';
  /** How the current viewer relates to the request. */
  viewerRole?: 'requester' | 'target' | 'planner';
  /** Whether the current viewer may approve planner-pending requests. */
  canApprove?: boolean;

  // --- Requester info ---
  requesterName: string;

  // --- Target shift (the one the requester wants) ---
  targetShiftName: string;
  targetShiftColor: string;
  targetStartTime: string;
  targetEndTime: string;
  targetDate: string;
  targetDateLabel: string;
  targetEmployeeName: string;
  targetPlanName: string;

  // --- Offered shift (swap only) ---
  offeredShiftName?: string;
  offeredShiftColor?: string;
  offeredStartTime?: string;
  offeredEndTime?: string;
  offeredDate?: string;
  offeredDateLabel?: string;
  offeredPlanName?: string;
}

/**
 * SwapRequestCard Component
 *
 * Displays a swap or takeover request in a card format.
 * Shows both sides of the exchange with action buttons
 * depending on the user's role (requester vs target).
 */
@Component({
  selector: 'zag-swap-request-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [Card, ZagIcon, ZagButton, TranslatePipe],
  host: {
    '[class.zag-swap-request-card]': 'true',
  },
  templateUrl: './swap-request-card.html',
  styleUrls: ['./swap-request-card.scss'],
})
export class SwapRequestCard {
  /** The request data to display */
  readonly request = input.required<SwapRequestCardData>();

  /** Whether an action is currently in progress */
  readonly actionInProgress = input(false);

  /** Emitted when the target user accepts an incoming request */
  readonly acceptClick = output<SwapRequestCardData>();

  /** Emitted when the target user declines an incoming request */
  readonly declineClick = output<SwapRequestCardData>();

  /** Emitted when the requester cancels their outgoing request */
  readonly cancelClick = output<SwapRequestCardData>();

  /** Whether this request is still actionable */
  protected readonly isActionable = computed(() => {
    const request = this.request();

    if (request.viewerRole === 'planner') {
      return request.canApprove === true && request.status === 'pending_planner_approval';
    }

    if (request.direction === 'incoming') {
      return request.status === 'pending';
    }

    return request.status === 'pending' || request.status === 'pending_planner_approval';
  });

  protected readonly canApprove = computed(() => {
    const request = this.request();
    return request.viewerRole === 'planner' && request.canApprove === true && request.status === 'pending_planner_approval';
  });

  protected readonly directionLabelKey = computed(() => {
    if (this.request().viewerRole === 'planner') {
      return 'shiftSwaps.plannerReview';
    }

    return this.request().direction === 'incoming' ? 'shiftSwaps.incomingFrom' : 'shiftSwaps.outgoingTo';
  });

  /** Status i18n key */
  protected readonly statusKey = computed(() => {
    const status = this.request().status.toLowerCase();
    return `shiftSwaps.requestStatus.${status}`;
  });

  onAccept(event: Event): void {
    event.stopPropagation();
    this.acceptClick.emit(this.request());
  }

  onDecline(event: Event): void {
    event.stopPropagation();
    this.declineClick.emit(this.request());
  }

  onCancel(event: Event): void {
    event.stopPropagation();
    this.cancelClick.emit(this.request());
  }
}
